import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router'; // Added RouterModule
import { Subscription } from 'rxjs';
import { filter, map, startWith, switchMap, take, tap } from 'rxjs/operators';

// PrimeNG
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ChatSession } from '../models/chat-session.model';
import { ProgressBarModule } from 'primeng/progressbar'; // Import ProgressBarModule
import { ChatSessionService } from '../services/chat-session.service';
import { ToastModule } from 'primeng/toast';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';


@Component({
  selector: 'app-ai-studio-container',
  imports: [
    CommonModule,
    ToastModule,
    AvatarModule,
    AvatarGroupModule,
    RouterModule, // For routerLink and RouterOutlet
    TabsModule,
    ButtonModule,
    TooltipModule,
    ProgressBarModule // Add ProgressBarModule here
  ],
  templateUrl: './ai-studio-container.component.html',
  styleUrl: './ai-studio-container.component.css'
})
export class AiStudioContainerComponent implements OnInit, OnDestroy {

  sessions: ChatSession[] = [];
  activeSessionId: number = 0;
  private sessionSub!: Subscription;
  private routerSub!: Subscription;

  constructor(
    public sessionService: ChatSessionService, // Public to access in template if needed directly (usually not)
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit(): void {
    // Combined subscription to handle initial load and subsequent route changes
    this.routerSub = this.sessionService.initialLoadComplete$.pipe(
      filter(initialLoadCompleted => initialLoadCompleted), // Wait for initial load
      take(1), // Only need this signal once for the initial setup
      switchMap(() => {
        // Once initial load is done, subscribe to session changes
        return this.sessionService.sessions$.pipe(
          tap((sessions: ChatSession[]) => { // Use tap for side-effects like updating this.sessions
            // De-duplicate sessions based on sessionId, keeping the first occurrence
            const uniqueSessionsMap = new Map<number, ChatSession>();
            sessions.forEach(session => {
              if (!uniqueSessionsMap.has(session.sessionId)) {
                uniqueSessionsMap.set(session.sessionId, session);
              }
            });
            this.sessions = Array.from(uniqueSessionsMap.values()).sort((a, b) => b.sessionId - a.sessionId);
            // Handle initial navigation or tab update after sessions are loaded
            const currentRouteSessionId = this.activatedRoute.firstChild?.snapshot.paramMap.get('sessionId');
            if (!currentRouteSessionId && this.sessions.length > 0 && (this.router.url === '/ai-studio' || this.router.url === '/ai-studio/')) {
              // If on base /ai-studio route and sessions exist, navigate to the first one
              this.sessionService.setActiveSessionId(this.sessions[0].sessionId);
              this.router.navigate(this.getSessionRouteCommands(this.sessions[0]), { replaceUrl: true });
            }
            // DO NOT create a new session automatically here.
            // If sessions.length is 0 and no sessionId in route, the view will show "no sessions" state.
          })
        );
      }),
      // Separately, listen to router events to update tab index on navigation
      // This needs to be a separate stream or managed carefully if combined
      // For simplicity, let's keep a separate subscription for router events for tab updates
    ).subscribe(); // This subscription handles the initial setup

    // Separate subscription for ongoing route changes to update active tab
    this.sessionSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.activatedRoute.firstChild?.snapshot.paramMap.get('sessionId'))
    ).subscribe(() => {
      // Ensure sessions are loaded before trying to update tab index
      if (this.sessions.length > 0) {
        this.updateActiveTabFromRoute();
      }
    });
  }

  private updateActiveTabFromRoute(): void {
    const currentSessionIdParam = this.activatedRoute.firstChild?.snapshot.paramMap.get('sessionId');
    if (currentSessionIdParam) {
      const resolvedSessionId = Number(currentSessionIdParam);
      if (!Number.isFinite(resolvedSessionId)) {
        return;
      }
      if (this.sessions.some((s) => s.sessionId === resolvedSessionId)) {
        this.activeSessionId = resolvedSessionId;
        this.sessionService.setActiveSessionId(resolvedSessionId);
      } // write an else condition here to load the session from API and then show the tab contents with chat
      else {
        // If the session is not in the current list, it might be a new session
        // or one that was just loaded. Attempt to load it.
        this.sessionService.getSession(resolvedSessionId).subscribe({
          next: (session) => {
            if (session) {
              this.activeSessionId = session.sessionId;
              this.sessionService.setActiveSessionId(session.sessionId);
              // The session will be added to the sessions$ observable by getSession,
              // which will then update this.sessions via the main subscription.
            } else {
              console.warn(`Session with ID ${currentSessionIdParam} not found after attempting to load.`);
              // Optionally navigate away or show an error if the session truly doesn't exist
            }
          },
          error: (err) => {
            console.error(`Error loading session ${currentSessionIdParam}:`, err);
            // Handle error, e.g., navigate to a default view
          }
        });
      }
      
    }
  }

  addNewSession(): void {
    this.sessionService.addSession().subscribe({
      next: (newSession) => {
        if (newSession && newSession.sessionId) {
          // Navigate to the new session's route
          this.router.navigate(this.getSessionRouteCommands(newSession), { replaceUrl: true });
        } else {
          console.error('Failed to create new session or session ID is missing in container.');
          // Optionally, show a user-facing error message here using MessageService if you have one injected
        }
      },
      error: (err) => {
        console.error('Error creating new session in container:', err);
        // Optionally, show a user-facing error message
      }
    });
  }

  getRouterLink(session: ChatSession): any[] {
    return this.getSessionRouteCommands(session);
  }

  private getSessionRouteCommands(session: ChatSession): any[] {
    return ['/ai-studio', 'session', session.sessionId, { outlets: { canvasOutlet: this.getCanvasOutletSegments(session) } }];
  }

  private getCanvasOutletSegments(session: ChatSession): string[] {
    const canvasPath = session.activeCanvasPath || 'canvas-view';
    return canvasPath.split('/');
  }

  onTabClose(event: MouseEvent, sessionToClose: ChatSession): void {
    console.log(`[onTabClose] Close event triggered for session: ${sessionToClose.sessionId} at index: ${event.target}`);
    // The built-in close icon is not part of a link, so we don't need to stop propagation
    // for navigation reasons. The component handles removing the tab from the view.
    // We just need to handle our application state.
    event.preventDefault();
    event.stopPropagation();

    if (sessionToClose) {
      console.log(`[onTabClose] Calling sessionService.closeSession for ${sessionToClose.sessionId}`);
      this.sessionService.closeSession(sessionToClose.sessionId).then(
        () => {
          console.log(`[onTabClose] Session ${sessionToClose.sessionId} closed successfully.`);
          // The session service already updates its internal list and navigates if needed.
          // No further action needed here for UI update as sessions$ observable handles it.
        }
      ).catch(error => {
        console.error(`Error closing session ${sessionToClose.sessionId}:`, error);
        // Optionally, show a user-facing error message
      });
      
    } else {
      console.warn('[onTabClose] sessionToClose was null or undefined.');
    }
  }

  ngOnDestroy(): void {
    this.sessionSub?.unsubscribe();
    this.routerSub?.unsubscribe();
  }
}
